function getClientRollbarConfigs() {
  const modernBrowsersRegexp = require('./browserlistModernRegexp');
  const { ENVIRONMENT, ROLLBAR_CLIENT_ACCESS_TOKEN, APP_BUILD_ID } = require('./constants');
  const hostSafeList = [
    'concern.net',
    'gifts.concern.net',
    'concern.org.uk',
    'gifts.concern.org.uk',
    'systemseed.host',
    'concern.local',
  ];

  return {
    enabled: !!ROLLBAR_CLIENT_ACCESS_TOKEN,
    captureUncaught: true,
    captureUnhandledRejections: true,
    environment: ENVIRONMENT ?? 'undefined',
    accessToken: ROLLBAR_CLIENT_ACCESS_TOKEN,
    scrubTelemetryInputs: true,
    checkIgnore: (isUncaught, args, payload) => {
      // Ignore errors if we can't figure out user browser.
      if (!navigator || !navigator.userAgent) {
        return true;
      }
      // Ignore errors if they come from old browsers.
      if (!modernBrowsersRegexp.test(navigator.userAgent)) {
        return true;
      }

      let errorMessage = payload?.body?.trace?.exception?.message;
      if (payload?.body?.trace_chain && payload.body.trace_chain.length > 0) {
        errorMessage = payload.body.trace_chain[0]?.exception?.message;
      }
      // Ignore specific errors to reduce noise.
      if (isUncaught && errorMessage && typeof errorMessage === 'string') {
        const ignoreMessages = [
          // Network errors. See https://github.com/sindresorhus/is-network-error/blob/main/index.js
          'network error', // Chrome
          'Failed to fetch', // Chrome
          'NetworkError when attempting to fetch resource.', // Firefox
          'The Internet connection appears to be offline.', // Safari 16
          'Network request failed', // `cross-fetch`
          'fetch failed', // Undici (Node.js)
          'terminated', // Undici (Node.js)
          ' A network error occurred.', // Bun (WebKit)
          'Network connection lost', // Cloudflare Workers (fetch)

          // Mobile Safari error when any fetch is failed. We already have
          // logging in our 'fetch' function.
          'Load failed',
          // Facebook/Instagram browser error.
          "Can't find variable: _AutofillCallbackHandler",
          // Browser extension generates this error.
          'Invalid call to runtime.sendMessage(). Tab not found.',
          // Just Abort error.
          'signal is aborted without reason',
          // Browser extension generates this error.
          'Illegal invocation',
          // IOS error related to Cookiebot script.
          "undefined is not an object (evaluating 'window.Cookiebot.handleCcpaOptinInFrontend')",
          // Errors from GTM script.
          'Logging is not enabled in all environments',
          // Errors from bat.bing.com script.
          "null is not an object (evaluating 'Q.userId')",
          // Ignore "Script error." exception for now, it throws an error in "HeadlessChrome" browser.
          // Every automated test thrown that error for some reason. Does not reproducible for usual browsers.
          // This was added during the implementation of the CWIESP-2964 story.
          'Script error.',
          // Error during loading Stripe script.
          'Failed to load Stripe.js',
          // Errors from clarity.ms script.
          "undefined is not an object (evaluating 'contentScriptData.init_ts')",
        ];

        const ignoreMessagesStartsWith = [
          // Errors in Mobile Safari from Ads script.
          "Can't find variable: al_on",
        ];

        if (ignoreMessages.includes(errorMessage)) {
          return true;
        }

        const shouldBeIgnored = ignoreMessagesStartsWith.some((message) =>
          errorMessage.startsWith(message),
        );
        if (shouldBeIgnored) {
          return true;
        }
      }

      // Ensuring error is sent from the allowed list of domains. Ignore
      // errors sent from other domains. See #186330176 for details.
      const url = payload.request.url ?? '';
      return !url || hostSafeList.every((host) => !url.includes(host));
    },
    payload: {
      environment: ENVIRONMENT,
      client: {
        javascript: {
          source_map_enabled: true,
          code_version: APP_BUILD_ID,
          guess_uncaught_frames: true,
        },
      },
    },
  };
}

function getRollbar() {
  if (typeof window === 'undefined') {
    const { ENVIRONMENT } = require('./constants');
    const Rollbar = require('rollbar');
    return new Rollbar({
      enabled: !!process.env.ROLLBAR_SERVER_ACCESS_TOKEN,
      captureUncaught: true,
      captureUnhandledRejections: true,
      environment: ENVIRONMENT ?? 'undefined',
      accessToken: process.env.ROLLBAR_SERVER_ACCESS_TOKEN ?? undefined,
    });
  } else {
    const clientConfig = getClientRollbarConfigs();
    const Rollbar = require('rollbar');
    return new Rollbar(clientConfig);
  }
}

module.exports = getRollbar();
